import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject, Signal } from '@angular/core';
import { Router } from '@angular/router';
import {
  AddPermissionRequest,
  CreatePermissionAttributeRequest,
  GroupedRoles,
  Permission,
  PermissionAttribute,
  RoleType,
  UpdatePermissionRequest,
  UpdateRoleForEntityRequest,
} from '@models/authorization';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, EMPTY, pipe, switchMap, tap } from 'rxjs';
import {
  addEntity,
  createCustomStateSetOnRegexHandler,
  createCustomStateSetOnUrlHandler,
  createStateResetHandler,
  deleteEntity,
  getAll,
  guidRegex,
  StateDictionary,
  updateEntity,
} from '../../helpers';
import { sortAlphabetically } from '../../helpers/helper';
import { PermissionService } from './permissions.service';

interface PermissionsState {
  permissions: Permission[];
  isLoading: boolean;
  isCreateLoading: boolean;
  selectedPermissionId: string | null;
  roles: GroupedRoles[];
  isRolesLoading: boolean;
  attributes: PermissionAttribute[];
  isAttributesLoading: boolean;
  isAttributeCreateLoading: boolean;
}

const initialState: PermissionsState = {
  permissions: [],
  isLoading: false,
  isCreateLoading: false,
  selectedPermissionId: null,
  roles: [],
  isRolesLoading: false,
  attributes: [],
  isAttributesLoading: false,
  isAttributeCreateLoading: false,
};

export const permissionsDictionary: StateDictionary<Permission> = {
  entityList: 'permissions',
  listLoading: 'isLoading',
  selectedEntity: 'selectedPermission',
  selectedEntityLoading: '', // isLoading takes care of this
  entityId: 'id',
  createLoading: 'isCreateLoading',
  sortFn: (a: Permission, b: Permission) => a.name.localeCompare(b.name),
};

export const PermissionsStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withDevtools('permissions-store'),

  withComputed(state => ({
    selectedPermission: computed(() => {
      const selectedId = state.selectedPermissionId();
      return state.permissions().find(permission => permission.id === selectedId) ?? null;
    }),
    isSelectedPermissionLoading: computed(() => state.isLoading()),
  })),

  withMethods((store, permissionService = inject(PermissionService), router = inject(Router)) => {
    createStateResetHandler<PermissionsState>('/permissions')(store, initialState);
    createCustomStateSetOnUrlHandler<PermissionsState>('/permissions', { selectedPermissionId: null })(store);
    createCustomStateSetOnRegexHandler<PermissionsState>(new RegExp(`^/permissions/(${guidRegex})/attributes$`))(store, {
      attributes: [],
    });

    const repositoryMethods = {
      getAll: getAll<Permission>({
        store,
        service: permissionService,
        dictionary: permissionsDictionary,
      }),

      addNew: addEntity<Permission, AddPermissionRequest>({
        store,
        service: permissionService,
        dictionary: permissionsDictionary,
        onSuccessCallback: newPermission => {
          patchState(store, { selectedPermissionId: newPermission.id });
          router.navigate(['permissions', newPermission.id, 'details']);
        },
      }),

      update: updateEntity<Permission, UpdatePermissionRequest>({
        store,
        service: permissionService,
        dictionary: permissionsDictionary,
        onSuccessCallback: updatedPermission => {
          patchState(store, { selectedPermissionId: updatedPermission.id });
        },
      }),

      delete: deleteEntity<Permission>({
        store,
        service: permissionService,
        dictionary: permissionsDictionary,
        onSuccessCallback: () => {
          patchState(store, { selectedPermissionId: null });
          router.navigate(['permissions']);
        },
      }),

      selectPermission: (id: string): void => {
        const permissions = store.permissions();
        const isLoading = store.isLoading();
        if (!permissions.find(permission => permission.id === id) && !isLoading) {
          repositoryMethods.getAll();
        }
        patchState(store, { selectedPermissionId: id });
      },

      getSelectedPermissionRoles: rxMethod<{ id: string; filter: RoleType }>(
        pipe(
          tap(() => patchState(store, { isRolesLoading: true })),
          switchMap(({ id, filter }) =>
            permissionService.getPermissionRoles(id, filter).pipe(
              tap(roles => patchState(store, { roles, isRolesLoading: false })),
              catchError(() => {
                patchState(store, { isRolesLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),
      patchSelectedPermissionRoles: rxMethod<{ id: string; roles: UpdateRoleForEntityRequest[]; filter: RoleType }>(
        pipe(
          tap(() => patchState(store, { isRolesLoading: true })),
          switchMap(({ id, roles, filter }) =>
            permissionService.patchPermissionRoles(id, roles, filter).pipe(
              tap(newRoles => patchState(store, { roles: newRoles, isRolesLoading: false })),
              catchError(() => {
                patchState(store, state => ({ roles: [...state.roles], isRolesLoading: false }));
                return EMPTY;
              }),
            ),
          ),
        ),
      ),
      getPermissionAttributes: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { isAttributesLoading: true })),
          switchMap(permissionId =>
            permissionService.getPermissionAttributes(permissionId).pipe(
              tap(attributes =>
                patchState(store, {
                  isAttributesLoading: false,
                  attributes: sortAlphabetically([...attributes], (a, b) => a.name.localeCompare(b.name)),
                }),
              ),
              catchError(() => {
                patchState(store, { isAttributesLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),
      addPermissionAttribute: rxMethod<CreatePermissionAttributeRequest>(
        pipe(
          tap(() => patchState(store, { isAttributeCreateLoading: true })),
          switchMap(attribute =>
            permissionService.addPermissionAttribute(attribute).pipe(
              tap(newAttr =>
                patchState(store, state => ({
                  isAttributeCreateLoading: false,
                  attributes: sortAlphabetically([...state.attributes, newAttr], (a, b) => a.name.localeCompare(b.name)),
                })),
              ),
              catchError(() => {
                patchState(store, { isAttributeCreateLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),
      deletePermissionAttribute: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { isAttributesLoading: true })),
          switchMap(attributeId =>
            permissionService.deletePermissionAttribute(attributeId).pipe(
              tap(() =>
                patchState(store, state => ({
                  isAttributesLoading: false,
                  attributes: state.attributes.filter(attr => attr.id !== attributeId),
                })),
              ),
              catchError(() => {
                patchState(store, { isAttributesLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),
    };

    const navigationMethods = {
      goToPermissionActions: async (id: string): Promise<boolean> => {
        const result = await router.navigate(['permissions', id]);
        if (!result) return false;
        repositoryMethods.selectPermission(id);
        return true;
      },
    };

    const resetState = () => patchState(store, initialState);

    return {
      ...repositoryMethods,
      ...navigationMethods,
      resetState,
    };
  }),
);

export interface PermissionsStoreType {
  readonly permissions: Signal<Permission[]>;
  readonly isLoading: Signal<boolean>;
  readonly isCreateLoading: Signal<boolean>;
  readonly selectedPermission: Signal<Permission | null>;
  readonly selectedPermissionId: Signal<string | null>;
  readonly isSelectedPermissionLoading: Signal<boolean>;
  readonly roles: Signal<GroupedRoles[]>;
  readonly isRolesLoading: Signal<boolean>;
  readonly attributes: Signal<PermissionAttribute[]>;
  readonly isAttributesLoading: Signal<boolean>;
  readonly isAttributeCreateLoading: Signal<boolean>;

  readonly getAll: () => void;
  readonly addNew: (permission: AddPermissionRequest) => void;
  readonly update: (input: { id: string; dto: UpdatePermissionRequest }) => void;
  readonly delete: (id: string) => void;
  readonly selectPermission: (id: string) => void;

  readonly getSelectedPermissionRoles: (input: { id: string; filter: RoleType }) => void;
  readonly patchSelectedPermissionRoles: (input: { id: string; roles: UpdateRoleForEntityRequest[]; filter: RoleType }) => void;

  readonly getPermissionAttributes: (permissionId: string) => void;
  readonly addPermissionAttribute: (attribute: CreatePermissionAttributeRequest) => void;
  readonly deletePermissionAttribute: (attributeId: string) => void;

  readonly goToPermissionActions: (id: string) => Promise<boolean>;
  readonly resetState: () => void;
}
